import { forwardRef, useId, type InputHTMLAttributes } from 'react';

/**
 * Text input with an always-present label — BUILD-PLAN §40/§44.
 *
 * §40 requires proper labels. Placeholder-as-label is a common luxury-site
 * pattern and an accessibility failure: the label vanishes once typing
 * starts, and screen readers do not reliably announce it. The label is
 * therefore a real <label>, visually styled but never removed.
 *
 * Errors are wired with aria-describedby and aria-invalid so assistive
 * technology announces the message rather than only showing red text.
 */

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, required, ...props },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium tracking-wide text-ink">
        {label}
        {required && (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <input
        id={id}
        ref={ref}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={[
          'min-h-11 rounded-sm border bg-surface px-3.5 text-sm text-ink transition-colors',
          'placeholder:text-ink-300',
          error ? 'border-danger' : 'border-line focus:border-accent',
        ].join(' ')}
        {...props}
      />

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}

      {error && (
        // role="alert" so the message is announced when it appears after a
        // failed submission, not only when the field is focused.
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
